import { LitElement, html, nothing } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../../shared/services/debug.service.js';
import { ApplicationService } from '../../../../services/application.service.js';
import { style } from './address.style.js';
import { REFI_PERSONAL_CONST } from '../../../../shared/constants/app.constants.js';
import { globalStyle } from '../../../../global-styles.style.js';
import '@cof/fs-select-wc/fs-select.js';
import { stratum } from '../../../../../constants/stratum.js';

class RefiPersonalAddressComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepAddress');
    this.values = {};
    this.coAppPrefix = '';
    this.invalidAddress = [];
    this.aptPattern = '^([a-zA-Z0-9]| |-|\\.)*$';
    this.active = false;
    this.isIconic = false;
    this.ownerStatusMissing = false;
  }

  static styles = [style, globalStyle];

  static properties = {
    applicant: { type: Object, reflect: true },
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
    invalidAddress: { type: Array, reflect: true },
    active: { type: Boolean, reflect: true },
    isIconic: { type: Boolean, attribute: 'is-iconic', reflect: true },
    ownerStatusMissing: { type: Boolean, reflect: true },
  };

  connectedCallback() {
    super.connectedCallback();
    this.coAppPrefix = this.isPrimary ? '' : 'co-';
    this.streetId = `${this.coAppPrefix}street`;
    this.aptId = `${this.coAppPrefix}aptNumber`;
    this.cityId = `${this.coAppPrefix}city`;
    this.stateCodeId = `${this.coAppPrefix}stateCode`;
    this.zipId = `${this.coAppPrefix}zipCode`;
    this.ownerStatusId = `${this.coAppPrefix}ownerStatus`;
    this.monthlyPaymentId = `${this.coAppPrefix}housingMonthlyPayment`;
    this.ids = [
      this.streetId,
      this.aptId,
      this.cityId,
      this.stateCodeId,
      this.zipId,
      this.ownerStatusId,
      this.monthlyPaymentId,
    ];
    this.application = ApplicationService.Application;
    this.values = JSON.parse(
      JSON.stringify(
        this.isPrimary
          ? this.application.primaryApplicant.currentAddress
          : this.application.coApplicant.currentAddress
      )
    );
  }

  render() {
    return html`
      ${this.active
        ? html`<div class="personal__street">
              <fs-text-field
                class="includes-error-message"
                id="${this.streetId}"
                autocomplete="street-address"
                name="street-address"
                helper-text="${this.isIconic ? 'Sorry, no P.O. Boxes' : ''}"
                type="text"
                value-missing-message="Please enter a home address"
                maxLength="50"
                minLength="5"
                pattern=" *[a-zA-Z0-9]+(?: [a-zA-Z0-9.]+)+ *"
                required
                @change="${(ev) => this.changed('street', ev)}"
                @blur="${(ev) => {
                  this.invalidAddressCheck(this.streetId);
                  if (ev.target.validity.valid) {
                    stratum.publishTag(
                      TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD,
                      { replacements: { value: this.streetId } }
                    );
                  } else {
                    stratum.publishTag(
                      TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                      {
                        replacements: {
                          value:
                            'Please use valid characters and more than one word',
                        },
                      }
                    );
                  }
                }}"
                value="${this.values.street}"
                pattern-mismatch-message="Please use valid characters and more than one word"
              >
                Home address${!this.isIconic ? ' (Sorry, no P.O. Boxes)' : ''}
              </fs-text-field>
              <fs-body-text
                size="small"
                variant="feedback-negative"
                ?hidden="${!this.invalidAddress[this.streetId]}"
                id="${`${this.streetId}invalidAddress`}"
              >
                PO Box is not allowed
              </fs-body-text>
            </div>
            <div class="personal__apt">
              <fs-text-field
                id="${this.aptId}"
                autocomplete="address-line2"
                name="apt-address"
                type="text"
                value-missing-message="Please use valid characters"
                maxLength="8"
                pattern="${this.aptPattern}"
                @change="${(ev) => this.changed('aptNumber', ev)}"
                @blur="${(ev) => {
                  if (ev.target.validity.valid) {
                    stratum.publishTag(
                      TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD,
                      { replacements: { value: this.aptId } }
                    );
                  } else {
                    stratum.publishTag(
                      TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                      { replacements: { value: 'Please use valid characters' } }
                    );
                  }
                }}"
                value="${this.values.aptNumber}"
                pattern-mismatch-message="Please use valid characters"
              >
                Suite/Apt #
              </fs-text-field>
            </div>
            <div class="personal__city">
              <fs-text-field
                id="${this.cityId}"
                autocomplete="address-level2"
                name="city-address"
                type="text"
                pattern="[a-zA-Z][a-zA-Z ]+"
                value-missing-message="Please enter a city"
                maxLength="25"
                required
                @change="${(ev) => this.changed('city', ev)}"
                @blur="${(ev) => {
                  if (ev.target.validity.valid) {
                    stratum.publishTag(
                      TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD,
                      { replacements: { value: this.cityId } }
                    );
                  } else {
                    stratum.publishTag(
                      TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                      { replacements: { value: 'Please use valid characters' } }
                    );
                  }
                }}"
                value="${this.values.city}"
                pattern-mismatch-message="Please use valid characters"
              >
                City
              </fs-text-field>
            </div>
            <div class="personal__state">
              <fs-select
                id="${this.stateCodeId}"
                name="state-select"
                @blur="${(ev) => {
                  if (ev.target.validity.valid) {
                    stratum.publishTag(
                      TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD,
                      { replacements: { value: this.stateCodeId } }
                    );
                  } else {
                    stratum.publishTag(
                      TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                      { replacements: { value: 'This field is required' } }
                    );
                  }
                }}"
                @change="${(ev) => {
                  this.invalidAddressCheck(this.stateCodeId);
                  this.changed('stateCode', ev);
                }}"
                value="${this.values.stateCode}"
                required
              >
                <span slot="label">State</span>
                <option value="">Select state</option>
                ${REFI_PERSONAL_CONST.US_STATES.map(
                  (state) =>
                    html` <option value="${state.value}">${state.name}</option>`
                )}
              </fs-select>
              <fs-body-text
                size="small"
                variant="feedback-negative"
                ?hidden="${!this.invalidAddress[this.stateCodeId]}"
                id="${`${this.stateCodeId}-invalidAddress`}"
              >
                We're sorry. We do not offer auto refinancing in this state.
              </fs-body-text>
            </div>
            <div class="personal__zip">
              <fs-text-field
                id="${this.zipId}"
                autocomplete="postal-code"
                name="zip-address"
                type="tel"
                value-missing-message="Please enter a valid zip code"
                maxLength="5"
                minLength="5"
                pattern="(?!20189|20521|11111|33333|66666|77777|88888|99999)[0-9]{5}"
                required
                @change="${(ev) => this.changed('zipCode', ev)}"
                @blur="${(ev) => {
                  if (ev.target.validity.valid) {
                    stratum.publishTag(
                      TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD,
                      { replacements: { value: this.zipId } }
                    );
                  } else {
                    stratum.publishTag(
                      TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                      {
                        replacements: {
                          value: 'Please enter a valid zip code',
                        },
                      }
                    );
                  }
                }}"
                value="${this.values.zipCode}"
                pattern-mismatch-message="Please enter a valid zip code"
              >
                ZIP
              </fs-text-field>
            </div>
            ${this.renderNDIFields()}
            </div>`
        : nothing}
    `;
  }

  renderNDIFields() {
    return html`
      <div class="personal__owner-status">
        <fs-choice-button-group
          direction="row"
          field-label="${this.isPrimary
            ? 'Do you own or rent?'
            : `Does ${this.application.coApplicant.firstName} own or rent?`}"
        >
          <fs-choice-button
            id="${this.ownerStatusId}Own"
            name="ownerStatus"
            alignment="center"
            label-text="Own"
            type="radio"
            value="OWN"
            value-missing-message="Please select an option"
            required
            ?selected="${this.values.ownerStatus === 'OWN'}"
            data-e2e-ref="ownerStatusOwn"
            @change="${(ev) => {
              this.changed('ownerStatus', ev);
              this.ownerStatusMissing = false;
            }}"
            @blur="${(ev) => {
              if (ev.target.validity.valid) {
                stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                  replacements: { value: this.ownerStatusId },
                });
              } else {
                stratum.publishTag(
                  TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                  { replacements: { value: 'Please select an option' } }
                );
              }
            }}"
          ></fs-choice-button>
          <fs-choice-button
            id="${this.ownerStatusId}Rent"
            name="ownerStatus"
            alignment="center"
            label-text="Rent"
            type="radio"
            value="RENT"
            ?selected="${this.values.ownerStatus === 'RENT'}"
            value-missing-message="Please select an option"
            required
            data-e2e-ref="ownerStatusRent"
            @change="${(ev) => {
              this.changed('ownerStatus', ev);
              this.ownerStatusMissing = false;
            }}"
            @blur="${(ev) => {
              if (ev.target.validity.valid) {
                stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                  replacements: { value: this.ownerStatusId },
                });
              } else {
                stratum.publishTag(
                  TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                  { replacements: { value: 'Please select an option' } }
                );
              }
            }}"
          ></fs-choice-button>
          <fs-choice-button
            id="${this.ownerStatusId}Other"
            name="ownerStatus"
            alignment="center"
            label-text="Other"
            type="radio"
            value="OTHER"
            ?selected="${this.values.ownerStatus === 'OTHER'}"
            value-missing-message="Please select an option"
            required
            data-e2e-ref="ownerStatusOther"
            @change="${(ev) => {
              this.changed('ownerStatus', ev);
              this.ownerStatusMissing = false;
            }}"
            @blur="${(ev) => {
              if (ev.target.validity.valid) {
                stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                  replacements: { value: this.ownerStatusId },
                });
              } else {
                stratum.publishTag(
                  TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                  { replacements: { value: 'Please select an option' } }
                );
              }
            }}"
          ></fs-choice-button>
        </fs-choice-button-group>
        <fs-body-text
          size="small"
          variant="feedback-negative"
          ?hidden="${!this.ownerStatusMissing}"
          id="ownerStatusToggleInvalid"
        >
          Please select an option
        </fs-body-text>
        <div class="personal__housing-monthly-payment">
          <fs-currency-field
            id="${this.monthlyPaymentId}"
            name="housing-monthly-payment"
            min="0"
            max="199999"
            decimal-separator="none"
            maximum-fraction-digits="0"
            minimum-decimal-percision="0"
            autocomplete="on"
            required
            value="${this.values.monthlyPayment}"
            data-e2e-ref="housingMonthlyPayment"
            @input="${(ev) => {
              this.changed(
                'monthlyPayment',
                ev,
                ev.target?.value.replace(/[$,]/g, '')
              );
            }}"
            @blur="${(ev) => {
              this.changed(
                'monthlyPayment',
                ev,
                ev.target?.value.replace(/[$,]/g, '')
              );
              if (ev.target.validity.valid) {
                stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                  replacements: {
                    value: this.monthlyPaymentId,
                  },
                });
              } else {
                stratum.publishTag(
                  TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                  {
                    replacements: {
                      value: 'Please enter a valid amount',
                    },
                  }
                );
              }
            }}"
            value-missing-message="Please enter a valid amount"
            range-underflow-message="Amount entered should be at least $0"
          >
            How much is
            ${this.isPrimary
              ? 'your'
              : `${this.application.coApplicant.firstName}’s`}
            monthly housing expense?
          </fs-currency-field>
        </div>
      </div>
    `;
  }

  changed(name, ev, value = ev.target?.value) {
    if (ev.target.validity.valid) {
      if (name === 'monthlyPayment') {
        // Converting negative pasted values to positive values, as fs-currency-field bug doesn't currently invalidate it
        this.values[name] = Math.abs(value);
      } else {
        this.values[name] = value.trim();
      }
    }
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: {
          name,
          isValid:
            (ev?.target?.required && ev.target.validity.valid) ||
            ev.target.validity.valid,
        },
      })
    );
    this.requestUpdate();
  }

  checkOwnerStatus() {
    if (!this.values.ownerStatus) {
      stratum.publishTag(
        TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
        {
          replacements: {
            value: 'Please select an option',
          },
        }
      );

      this.ownerStatusMissing = true;
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: `${this.ownerStatusId.replace(this.coAppPrefix, '')}-invalid`,
            isValid: false,
          },
        })
      );
    } else {
      this.ownerStatusMissing = false;
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: `${this.ownerStatusId.replace(this.coAppPrefix, '')}-invalid`,
            isValid: true,
          },
        })
      );
    }
  }

  runBusinessChecks() {
    for (const id of this.ids) {
      this.invalidAddressCheck(id);
    }
  }

  invalidAddressCheck(id) {
    const field = this.shadowRoot.getElementById(id);
    if (id === this.streetId) {
      this.invalidAddress[id] = this.isPoBox(field.value);
    } else if (id === this.stateCodeId) {
      this.invalidAddress[id] =
        REFI_PERSONAL_CONST.INELIGIBLE_REFI_STATES.includes(field.value);
    }
    if (this.invalidAddress[id]) {
      stratum.publishTag(
        TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
        {
          replacements: {
            value:
              id === this.streetId
                ? 'PO Box is not allowed'
                : "We're sorry. We do not offer auto refinancing in this state.",
          },
        }
      );
    }
    this.requestUpdate();
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: {
          name: `${id.replace(this.coAppPrefix, '')}-invalid`,
          isValid: !this.invalidAddress[id],
        },
      })
    );
  }

  isPoBox(streetValue) {
    let tempVal = streetValue.replace(/[^a-zA-Z0-9]+/g, '');
    tempVal = tempVal.substring(tempVal.toUpperCase().indexOf('P'));

    const str1 =
      '^([Pp]([Oo][Ss][Tt])?\\s{0,10}\\.?\\s{0,10})([Oo]([fF][Ff][Ii][Cc][Ee])?\\s{0,10}?\\s{0,10})([Bb]([Oo])?[Xx])(.*)$';

    const str2 = '^(.*)([Pp])([Bb])([Xx])(.*)$';
    const str3 = '^(.*)([Pp][Oo][Ss][Tt]\\s*)([Oo][fF][Ff][Ii][Cc][Ee])(.*)$';
    const str4 = '^(.*)([Pp][Oo][Ss][Tt]\\s*)([Bb][Oo][Xx])(.*)$';
    const str5 = '^([Pp])([OoMm])([Bb])(.*)$';
    return (
      tempVal.match(new RegExp(str1)) ||
      tempVal.match(new RegExp(str2)) ||
      tempVal.match(new RegExp(str3)) ||
      tempVal.match(new RegExp(str4)) ||
      tempVal.match(new RegExp(str5))
    );
  }
}

customElements.define('refi-personal-address', RefiPersonalAddressComponent);
